import React from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { ActivityWizardData } from "../../../types";
import { useLoTranslationProps } from "../../../../hooks/useLoTranslation";

interface BasicFieldsSectionProps extends WizardStepProps<ActivityWizardData> {
  durationRequired: boolean;

  showMinCompletion?: boolean;
}

const BasicFieldsSection: React.FC<BasicFieldsSectionProps> = ({
  data,
  setData,
  errors,
  durationRequired,
  showMinCompletion = false,
}) => {
  const { t } = useTranslation("wizard-activity");

  const titleTrans = useLoTranslationProps("title", {
    header: t("fields.title"),
    maxValue: 70,
  });
  const descTrans = useLoTranslationProps("description", {
    header: t("fields.description"),
    maxValue: 256,
    multiline: true,
    rows: 3,
  });

  const errMsg = (key: keyof typeof errors): string =>
    errors[key as string] ? t(`errors.${errors[key as string]}`) || "" : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showMinCompletion ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InputCst
              id="activity-title"
              label={`${t("fields.title")} *`}
              value={data.title}
              onChange={(e: any) => setData({ title: e.target.value })}
              maxValue={70}
              error={!!errors.title}
              errorMessage={errMsg("title")}
              {...titleTrans}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputCst
              id="activity-digited-code"
              label={t("fields.digited-code") || ""}
              value={data.digited_code}
              onChange={(e: any) => setData({ digited_code: e.target.value })}
              maxValue={30}
              error={!!errors.digited_code}
              errorMessage={errMsg("digited_code")}
            />
          </Grid>
        </Grid>
      ) : (
        <InputCst
          id="activity-title"
          label={`${t("fields.title")} *`}
          value={data.title}
          onChange={(e: any) => setData({ title: e.target.value })}
          maxValue={70}
          error={!!errors.title}
          errorMessage={errMsg("title")}
          {...titleTrans}
        />
      )}

      <InputCst
        id="activity-description"
        label={`${t("fields.description")} *`}
        value={data.description}
        onChange={(e: any) => setData({ description: e.target.value })}
        maxValue={256}
        multiline
        rows={3}
        error={!!errors.description}
        errorMessage={errMsg("description")}
        {...descTrans}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InputCst
            id="activity-duration"
            label={
              durationRequired
                ? `${t("fields.duration")} *`
                : t("fields.duration") || ""
            }
            value={data.duration}
            onChange={(e: any) => setData({ duration: e.target.value })}
            type="number"
            min={0}
            error={!!errors.duration}
            errorMessage={errMsg("duration")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {showMinCompletion ? (
            <InputCst
              id="activity-min-completion"
              label={`${t("fields.min-completion-perc")} *`}
              value={data.min_completition_perc}
              onChange={(e: any) =>
                setData({ min_completition_perc: e.target.value })
              }
              type="number"
              min={0}
              max={100}
              error={!!errors.min_completition_perc}
              errorMessage={errMsg("min_completition_perc")}
            />
          ) : (
            <InputCst
              id="activity-digited-code"
              label={t("fields.digited-code") || ""}
              value={data.digited_code}
              onChange={(e: any) => setData({ digited_code: e.target.value })}
              maxValue={30}
              error={!!errors.digited_code}
              errorMessage={errMsg("digited_code")}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicFieldsSection;
